"use client";

// admin-ui-rework, Этап 1: единый каркас модального окна — затемнение,
// заголовок, закрытие по Esc и по клику на оверлей.
// Пакет не таскает i18n: доступное имя кнопки закрытия приходит пропом
// (closeAriaLabel обязателен — у неё нет видимого текста).
import { type ReactNode, useEffect, useId } from "react";
import { X } from "lucide-react";
import { useFocusTrap } from "./use-focus-trap";
import { IconButton } from "./icon-button";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Подвал окна (кнопки действий) — опционально. */
  footer?: ReactNode;
  /** Явное ограничение ширины utility-классом; по умолчанию ширина задаётся темой. */
  maxWidthClass?: string;
  /** Дополнительные классы карточки диалога (например, max-h/flex). */
  cardClassName?: string;
  /** Дополнительные классы обёртки содержимого. По умолчанию интервал задаёт тема. */
  bodyClassName?: string;
  /** data-testid карточки диалога (у оверлея — "data-testid" проп). */
  cardTestId?: string;
  /** data-testid кнопки закрытия. */
  closeTestId?: string;
  /** aria-label кнопки закрытия — обязателен (видимого текста нет). */
  closeAriaLabel: string;
  /** Скрывает стандартный заголовок для полноэкранного кастомного содержимого. */
  hideHeader?: boolean;
  "data-testid"?: string | undefined;
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  maxWidthClass,
  cardClassName = "",
  bodyClassName = "",
  cardTestId,
  closeTestId,
  closeAriaLabel,
  hideHeader = false,
  "data-testid": testId,
}: ModalProps) {
  const titleId = useId();
  // admin-ui-rework Этап 8: единое поведение диалогов — Esc закрывает,
  // Tab не выходит за пределы окна, фокус возвращается на триггер.
  const dialogRef = useFocusTrap<HTMLDivElement>(open, onClose);

  // Открытое окно блокирует прокрутку фона.
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      data-testid={testId}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "var(--ui-color-overlay)" }}
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid={cardTestId}
        className={`w-full border border-border bg-background outline-none ${maxWidthClass ?? ""} ${cardClassName}`}
        style={{
          borderRadius: "var(--ui-modal-radius)",
          padding: "var(--ui-modal-padding)",
          ...(maxWidthClass ? {} : { maxWidth: "var(--ui-modal-max-width)" }),
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {hideHeader ? (
          <h2 id={titleId} className="sr-only">{title}</h2>
        ) : (
          <div className="flex items-center justify-between" style={{ gap: "var(--ui-modal-header-gap)" }}>
            <h2 id={titleId} className="font-semibold text-text-primary" style={{ fontSize: "var(--ui-modal-title-font-size)" }}>{title}</h2>
            <IconButton label={closeAriaLabel} onClick={onClose} data-testid={closeTestId}>
              <X className="h-4 w-4" />
            </IconButton>
          </div>
        )}
        <div
          className={bodyClassName}
          style={bodyClassName ? undefined : { marginTop: "var(--ui-modal-body-gap)" }}
        >
          {children}
        </div>
        {footer ? (
          <div
            className="flex justify-end"
            style={{ marginTop: "var(--ui-modal-footer-gap)", gap: "var(--ui-modal-action-gap)" }}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}
